import React from 'react';
import { createPortal } from 'react-dom';
import { formatoCartellino } from './CartellinoStampa';
import {
  MM_PER_PX, COLORI, SPAZIATURE, ALTEZZE_BARCODE, SPESSORI_CORNICE,
} from './impostazioniCartellino';

// Cartellino "v2": cornice sottile, codice a barre in alto, filetto grigio, descrizione, e in basso a destra
// la scorta minima con il suo filetto. Impostazioni (corpo, allineamento, colore ...) in impostazioniCartellino.js.
// Lo stesso componente disegna anteprima e stampa. Tutte le misure sono in mm.

const MARGINE_ESTERNO = 1.2;   // tra il bordo del foglio e la cornice
const PADDING_INTERNO = 1.6;   // tra la cornice e il contenuto
const FILETTO_MM = 0.25;
const FILETTO_COLORE = '#d1d5db'; // grigio chiaro: separa senza pesare
const MARGINE_FILETTO = 1;     // spazio sopra e sotto un filetto
const CORPO_PIE = 3.3;         // scorta minima (mm)
const INTERLINEA = 1.12;
const MIN_FONT_MM = 2.2;
const LARGHEZZA_CARATTERE = 0.66; // larghezza media di una maiuscola in grassetto, in "em"

// ─── Stima del testo (senza misure nel DOM, così anteprima e stampa coincidono) ────────────────────────────

const righeStimate = (testo, fontMm, larghezzaMm) => {
  const perRiga = Math.max(1, Math.floor(larghezzaMm / (fontMm * LARGHEZZA_CARATTERE)));
  let righe = 1;
  let corrente = 0;
  String(testo ?? '').split(/\s+/).filter(Boolean).forEach((parola) => {
    const l = parola.length;
    if (l > perRiga) {
      righe += Math.ceil(l / perRiga) - (corrente === 0 ? 1 : 0);
      corrente = l % perRiga;
    } else if (corrente === 0) {
      corrente = l;
    } else if (corrente + 1 + l <= perRiga) {
      corrente += 1 + l;
    } else {
      righe += 1;
      corrente = l;
    }
  });
  return righe;
};

const adatta = (testo, altezzaMm, maxMm, larghezzaMm) => {
  for (let f = maxMm; f >= MIN_FONT_MM; f = Math.round((f - 0.1) * 10) / 10) {
    if (righeStimate(testo, f, larghezzaMm) * f * INTERLINEA <= altezzaMm) return f;
  }
  return MIN_FONT_MM;
};

/** Tutte le misure del cartellino per un formato e delle impostazioni. */
const calcola = (dati, imp, formato) => {
  const { larghezza: L, altezza: A } = formato;
  const cornice = imp.cornice ? SPESSORI_CORNICE.find((s) => s.id === imp.spessoreCornice)?.mm ?? 0.4 : 0;
  const gap = SPAZIATURE.find((s) => s.id === imp.spaziatura)?.mm ?? 1;
  const fattoreBarcode = ALTEZZE_BARCODE.find((a) => a.id === imp.altezzaBarcode)?.fattore ?? 0.34;

  const bordo = MARGINE_ESTERNO + cornice + PADDING_INTERNO;
  const larghezzaTesto = L - 2 * bordo;
  const barcodeH = Math.max(14, Math.round(A * fattoreBarcode * 10) / 10);

  const maxMm = imp.fontPx * MM_PER_PX;
  const corpoVoci = Math.max(MIN_FONT_MM, Math.round(maxMm * 0.62 * 10) / 10);
  const corpoNome = maxMm;
  const corpoEtichetta = maxMm * 0.88;

  const voci = dati.voci || [];
  const haTesto = Boolean(dati.nome || dati.etichetta || voci.length);
  const haFooter = Boolean(dati.scortaMin);

  const filetto = imp.divisori ? FILETTO_MM + 2 * MARGINE_FILETTO : 0;
  const footerH = haFooter ? CORPO_PIE * 1.2 + filetto : 0;
  const filettoBarcode = haTesto || haFooter ? filetto : 0;
  const vociMm = voci.reduce((t, v) => t + righeStimate(v, corpoVoci, larghezzaTesto) * corpoVoci * 1.15, 0) + Math.max(0, voci.length - 1) * gap;

  const blocchi = [dati.nome ? 'nome' : null, dati.etichetta ? 'etichetta' : null].filter(Boolean);
  const nGap = blocchi.length + (voci.length ? 1 : 0) - 1;
  const libero = A - 2 * bordo - barcodeH - filettoBarcode - footerH - vociMm - Math.max(0, nGap) * gap;
  const disponibile = Math.max(libero, 6);

  const lung = { nome: String(dati.nome ?? '').length, etichetta: String(dati.etichetta ?? '').length };
  const tot = blocchi.reduce((t, b) => t + Math.max(lung[b], 1), 0);
  const quota = (b) => (blocchi.length === 1 ? 1 : Math.min(0.7, Math.max(0.3, Math.max(lung[b], 1) / tot)));

  const dimensione = (testo, b, corpo) => (imp.adatta ? adatta(testo, disponibile * quota(b), corpo, larghezzaTesto) : corpo);

  return {
    L, A, gap, cornice, bordo, barcodeH, larghezzaTesto, corpoVoci, filetto,
    nome: dati.nome ? dimensione(dati.nome, 'nome', corpoNome) : null,
    etichetta: dati.etichetta ? dimensione(dati.etichetta, 'etichetta', corpoEtichetta) : null,
  };
};

// ─── Disegno ─────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * dati: { nome, etichetta, voci: string[], scortaMin: string|null }   (null = non mostrato)
 * imp:  impostazioni (vedi impostazioniCartellino.js);  svg: codice a barre (da useBarcodeCodice)
 */
export const CartellinoV2 = ({ dati, svg, imp }) => {
  const formato = formatoCartellino(imp.formato);
  const m = calcola(dati, imp, formato);
  const colore = COLORI.find((c) => c.id === imp.colore)?.hex ?? '#000000';
  const trasforma = imp.maiuscolo ? 'uppercase' : 'none';
  const filetto = (
    <div style={{ height: `${FILETTO_MM}mm`, background: FILETTO_COLORE, margin: `${MARGINE_FILETTO}mm 0`, flex: '0 0 auto' }} />
  );
  const haTesto = Boolean(dati.nome || dati.etichetta || dati.voci.length);

  return (
    <div
      className="cartellino"
      style={{
        width: `${m.L}mm`,
        height: `${m.A}mm`,
        boxSizing: 'border-box',
        padding: `${MARGINE_ESTERNO}mm`,
        background: '#ffffff',
        color: colore,
        fontFamily: 'Arial, Helvetica, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          border: m.cornice ? `${m.cornice}mm solid ${colore}` : 'none',
          padding: `${PADDING_INTERNO}mm`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* codice a barre: sempre nero (leggibilità dello scanner) */}
        <div
          style={{ flex: `0 0 ${m.barcodeH}mm`, height: `${m.barcodeH}mm`, width: '100%', display: 'flex', justifyContent: 'center', color: '#000000' }}
          dangerouslySetInnerHTML={{
            __html: svg ? svg.replace('<svg', '<svg style="width:100%;height:100%;display:block"') : '',
          }}
        />

        {imp.divisori && (haTesto || dati.scortaMin) && filetto}

        {/* descrizione */}
        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: `${m.gap}mm`,
            textAlign: imp.allineamento,
            overflow: 'hidden',
            marginTop: imp.divisori ? 0 : `${m.gap}mm`,
          }}
        >
          {dati.nome && (
            <div style={{ fontSize: `${m.nome}mm`, lineHeight: INTERLINEA, fontWeight: 800, textTransform: trasforma, wordBreak: 'break-word' }}>
              {dati.nome}
            </div>
          )}
          {dati.etichetta && (
            <div style={{ fontSize: `${m.etichetta}mm`, lineHeight: INTERLINEA, fontWeight: 700, textTransform: trasforma, wordBreak: 'break-word' }}>
              {dati.etichetta}
            </div>
          )}
          {dati.voci.map((v) => (
            <div key={v} style={{ fontSize: `${m.corpoVoci}mm`, lineHeight: 1.15, fontWeight: 600, wordBreak: 'break-word' }}>{v}</div>
          ))}
        </div>

        {/* scorta minima: fissa in basso a destra, con il suo filetto */}
        {dati.scortaMin && (
          <div style={{ flex: '0 0 auto' }}>
            {imp.divisori && filetto}
            <div style={{ textAlign: 'right', fontSize: `${CORPO_PIE}mm`, lineHeight: 1.2, fontWeight: 800, whiteSpace: 'nowrap', marginTop: imp.divisori ? 0 : `${m.gap}mm` }}>
              {dati.scortaMin}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Anteprima in scala (stesso markup della stampa, ingrandito con transform). */
export const CartellinoV2Anteprima = ({ dati, svg, imp, scala = 1.2 }) => {
  const f = formatoCartellino(imp.formato);
  return (
    <div
      style={{ width: `${f.larghezza * scala}mm`, height: `${f.altezza * scala}mm`, maxWidth: '100%' }}
      className="shadow-xl border border-border/60 bg-white overflow-hidden"
    >
      <div style={{ transform: `scale(${scala})`, transformOrigin: 'top left', width: `${f.larghezza}mm`, height: `${f.altezza}mm` }}>
        <CartellinoV2 dati={dati} svg={svg} imp={imp} />
      </div>
    </div>
  );
};

const cssStampa = ({ larghezza, altezza }) => `
  #cartellini-stampa { display: none; }
  @media print {
    @page { size: ${larghezza}mm ${altezza}mm; margin: 0; }
    html, body { margin: 0 !important; padding: 0 !important; background: #ffffff !important; overflow: visible !important; height: auto !important; }
    body > *:not(#cartellini-stampa) { display: none !important; }
    #cartellini-stampa { display: block !important; }
    #cartellini-stampa .cartellino-pagina {
      width: ${larghezza}mm; height: ${altezza}mm; overflow: hidden;
      page-break-after: always; break-after: page; page-break-inside: avoid; break-inside: avoid;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    #cartellini-stampa .cartellino-pagina:last-child { page-break-after: auto; break-after: auto; }
  }
`;

/** Area di stampa: un cartellino per pagina, @page della misura scelta, margini 0. Nascosta sullo schermo. */
export const AreaStampaV2 = ({ dati, svg, imp, copie }) => createPortal(
  <div id="cartellini-stampa" aria-hidden="true">
    <style>{cssStampa(formatoCartellino(imp.formato))}</style>
    {Array.from({ length: Math.max(1, copie) }, (_, i) => (
      <div key={i} className="cartellino-pagina">
        <CartellinoV2 dati={dati} svg={svg} imp={imp} />
      </div>
    ))}
  </div>,
  document.body,
);
